/**
 * API Service - Axios instance configured for Django backend.
 * Automatically attaches Firebase ID token to all requests.
 */
import axios from 'axios';
import { auth } from '../config/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach Firebase token
api.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken(true);
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to get Firebase token:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors uniformly
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        // Token expired or invalid - redirect to login
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }

      // Surface the most useful error string to callers
      const message =
        data?.error ||
        data?.detail ||
        (typeof data === 'string' ? data : null) ||
        `Request failed with status ${status}`;

      const enrichedError = new Error(message);
      enrichedError.status = status;
      enrichedError.data = data;
      return Promise.reject(enrichedError);
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error('Request timed out. Please check your connection.'));
    }

    return Promise.reject(new Error('Network error. Is the backend server running?'));
  }
);

// API endpoints
export const authAPI = {
  register: (data, config = {}) => api.post('/auth/register/', data, config),
  verifyToken: (idToken) => api.post('/auth/verify-token/', { id_token: idToken }),
  getProfile: () => api.get('/auth/profile/'),
  updateProfile: (data) => api.put('/auth/profile/', data),
  setRole: (data) => api.post('/auth/set-role/', data),
  healthCheck: () => api.get('/auth/health/'),
};

export const patientAPI = {
  getDashboard: () => api.get('/patients/dashboard/'),
  getProfile: () => api.get('/patients/profile/'),
  updateProfile: (data) => api.put('/patients/profile/', data),
  getMedicalHistory: (patientUid) => 
    api.get('/patients/medical-history/', { params: { patient_uid: patientUid } }),
  addMedicalHistory: (data) => api.post('/patients/medical-history/', data),
  searchDoctors: (params) => api.get('/patients/search-doctors/', { params }),
  getDoctorDetail: (uid) => api.get('/patients/doctor-detail/', { params: { uid } }),
  requestEmergency: (data) => api.post('/patients/emergency/', data),
};

export const doctorAPI = {
  getDashboard: () => api.get('/doctors/dashboard/'),
  getProfile: () => api.get('/doctors/profile/'),
  updateProfile: (data) => api.put('/doctors/profile/', data),
  getPatientDetail: (uid) => api.get('/doctors/patient-detail/', { params: { uid } }),
  getQueue: () => api.get('/doctors/queue/'),
  submitDiagnosis: (data) => api.post('/doctors/diagnosis/', data),
  getSchedule: () => api.get('/doctors/schedule/'),
  updateSchedule: (data) => api.post('/doctors/schedule/', data),
  acceptEmergency: (data) => api.post('/doctors/accept-emergency/', data),
};

export const adminAPI = {
  getDashboard: () => api.get('/admin-portal/dashboard/'),
  getPendingDoctors: () => api.get('/admin-portal/pending-doctors/'),
  verifyDoctor: (data) => api.post('/admin-portal/verify-doctor/', data),
  getUsers: (params) => api.get('/admin-portal/users/', { params }),
  updateUser: (data) => api.put('/admin-portal/users/', data),
  getSettings: () => api.get('/admin-portal/settings/'),
  updateSettings: (data) => api.put('/admin-portal/settings/', data),
  getPayments: (params) => api.get('/admin-portal/payments/', { params }),
  settlePayment: (data) => api.post('/admin-portal/payments/settle/', data),
};

export const appointmentAPI = {
  book: (data) => api.post('/appointments/book/', data),
  list: (params) => api.get('/appointments/', { params }),
  getDetail: (id) => api.get(`/appointments/${id}/`),
  updateStatus: (id, data) => api.put(`/appointments/${id}/`, data),
  getQueueStatus: (doctorUid) => 
    api.get('/appointments/queue-status/', { params: { doctor_uid: doctorUid } }),
};

export const consultationAPI = {
  create: (data) => api.post('/consultations/create/', data),
  join: (data) => api.post('/consultations/join/', data),
  end: (data) => api.post('/consultations/end/', data),
  history: () => api.get('/consultations/history/'),
};

export const prescriptionAPI = {
  create: (data) => api.post('/prescriptions/create/', data),
  list: () => api.get('/prescriptions/'),
  getDetail: (id) => api.get(`/prescriptions/${id}/`),
  update: (id, data) => api.put(`/prescriptions/${id}/`, data),
};

export const paymentAPI = {
  initiate: (data) => api.post('/payments/initiate/', data),
  getStatus: (id) => api.get(`/payments/status/${id}/`),
  history: () => api.get('/payments/history/'),
  setupNgrok: (data) => api.post('/payments/setup-ngrok/', data),
};

export const notificationAPI = {
  list: (unread) => api.get('/notifications/', { params: { unread } }),
  markRead: (id) => api.put(`/notifications/${id}/read/`),
  markAllRead: () => api.put('/notifications/mark-all-read/'),
  sendSMS: (data) => api.post('/notifications/send-sms/', data),
};

export default api;
